// ============================================================
// THE DISPLAY'S TIMELINE - easing, progress, and the short way round.
//
// Every move on the display (a camera turn, a strike, a fold) is a value
// eased between two others over a duration. A duration of zero is the
// reduced-motion case: the move is already over. Yaw is the one value that
// wraps, and the user ruled it always turns the short way (35° to 300° is
// −95°, never +265°).
//
// Pure. Zero React dependency.
// ============================================================
import { wrap180 } from '../battlefield/facing';

const clamp01 = (k: number) => Math.min(1, Math.max(0, k));

/** Cubic ease-in-out over [0, 1], clamped. */
export function easeInOut(k: number): number {
  const t = clamp01(k);
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

/** How far through a move that started at `start` and lasts `ms` we are at `now`, in [0, 1]. */
export function progress(now: number, start: number, ms: number): number {
  return ms <= 0 ? 1 : clamp01((now - start) / ms);
}

/** A value part of the way from one to another. */
export const lerp = (a: number, b: number, k: number) => a + (b - a) * k;

/** A point part of the way from one to another. */
export const lerpXY = (a: { x: number; y: number }, b: { x: number; y: number }, k: number) => ({
  x: lerp(a.x, b.x, k),
  y: lerp(a.y, b.y, k),
});

/**
 * One step of an eased value toward its target (`k`: the share of the gap to close, at most all of it), settled
 * exactly once within 0.002 - an eased value never quite arrives, and a weight tested for 0 or 1 would never be.
 */
export function approach(value: number, target: number, k: number): number {
  const next = lerp(value, target, Math.min(1, k));
  return Math.abs(next - target) < 0.002 ? target : next;
}

/** The signed turn from one yaw to another, the short way: in (−180, 180], the facing code's own fold. */
export function yawDelta(from: number, to: number): number {
  return wrap180(to - from);
}

/** A yaw part of the way from one heading to another, the short way. Unwrapped: may leave [0, 360). */
export function lerpYaw(from: number, to: number, k: number): number {
  return from + yawDelta(from, to) * k;
}
